import { useMap } from '@vis.gl/react-google-maps';
import GoogleMaps from 'components/google-maps/google-maps';
import { DeliveryBoardItem } from 'views/delivery-board/types/delivery-board-model.type';
import React, { useEffect, useMemo } from 'react';
import _ from 'underscore';
import MarkerContainer from './marker-container';
import { Factories } from './data/factories.data';
import { VesselContacts } from './data/vessel-contacts.data';

type Props = {
  items: DeliveryBoardItem[];
  showVessels: boolean;
  showFactories: boolean;
};

const DeliveryBoardMap: React.FC<Props> = ({ items, showVessels, showFactories }) => {
  const map = useMap();

  //#region STATES

  const factories = useMemo(() => {
    const factoryIds = _.chain(items)
      .uniq((item) => item.plantId)
      .map((item) => item.plantId)
      .value();

    return showFactories ? Factories.filter((item) => factoryIds?.includes(item.sapCode)).map((item) => item) : [];
  }, [items, showFactories, Factories]);

  const vessels = useMemo(() => {
    return showVessels
      ? _.flatten(
          Object.values(
            _.chain(items)
              .uniq(false, (i) => i.vessel)
              .filter((i) => !!i.vesselLocation?.latitude && !!i.vesselLocation?.longitude)
              .groupBy((item) => `${item.vesselLocation.latitude}_${item.vesselLocation.longitude}`)
              .value()
          )
        )
      : [];
  }, [items, showVessels]);

  const sites = useMemo(() => {
    return _.flatten(Object.values(_.groupBy(items, (item) => `${item.siteLocation.latitude}_${item.siteLocation.longitude}`))).map(
      (item) => ({ lat: Number(item.siteLocation.latitude), lng: Number(item.siteLocation.longitude), name: item.siteName })
    );
  }, [items]);

  //#endregion STATES

  //#region HOOKS

  useEffect(() => {
    // Map vessel hardcoded vessel contacts to vessels list
    vessels?.forEach((vessel) => {
      const matchingVessel = VesselContacts.find((vesselContact) => vesselContact.vesselName === vessel.vessel);
      if (matchingVessel) vessel.vesselContact = matchingVessel;
    });
  }, [vessels, VesselContacts]);

  useEffect(() => {
    if (map) {
      if (!items?.length) return;
      let bounds = new google.maps.LatLngBounds();

      const coords = [
        ...vessels.map((i) => ({ lat: Number(i.vesselLocation.latitude), lng: Number(i.vesselLocation.longitude), name: i.vessel })),
        ...sites,
        ...factories.map((factory) => ({
          lat: Number(factory.factoryLocation.latitude),
          lng: Number(factory.factoryLocation.longitude),
          name: factory.factoryName,
        })),
      ];

      coords?.forEach((item) => bounds.extend({ lat: item.lat, lng: item.lng }));

      map.fitBounds(bounds);
      map.panToBounds(bounds);
      map.setCenter({
        lat: bounds.getCenter().lat(),
        lng: bounds.getCenter().lng(),
      });
    }
  }, [vessels, sites, factories, map]);

  //#endregion HOOKS

  return (
    <GoogleMaps maxZoom={18}>
      <MarkerContainer sites={sites} vessels={vessels} factories={factories} />
    </GoogleMaps>
  );
};

export default React.memo(DeliveryBoardMap);
